import React from "react";
import {
  Modal,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Typography,
  message,
} from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import type { Location } from "../hooks/useFinanceData";

const { Text } = Typography;
const { Option } = Select;

type CustomValue = {
  description: string;
  amount: number;
  currency: "RSD" | "EUR";
  month: number;
  year: number;
  invoiceDate: string;
  dueDate?: string;
};

interface AddCustomValueModalProps {
  visible: boolean;
  editMode?: boolean;
  supplierData: {
    supplierId: string;
    supplierName: string;
    categoryId: string;
    locationId: string;
  } | null;
  locations: Location[];
  customValue: CustomValue;
  onValueChange: (value: Partial<CustomValue>) => void;
  onCancel: () => void;
  onSubmit: () => void;
}

export const AddCustomValueModal: React.FC<AddCustomValueModalProps> = ({
  visible,
  editMode = false,
  supplierData,
  locations,
  customValue,
  onValueChange,
  onCancel,
  onSubmit,
}) => {
  const { t } = useTranslation();

  if (!supplierData) return null;

  const selectedLocation = locations.find(
    (loc) => loc._id === supplierData.locationId,
  );

  return (
    <Modal
      title={editMode ? t("Edit Custom Value") : t("Add Custom Value")}
      open={visible}
      onOk={onSubmit}
      onCancel={onCancel}
      okText={editMode ? t("Update") : t("Add")}
      cancelText={t("Cancel")}
      className="standardHeader standardFooter"
    >
      <div>
        <div style={{ marginBottom: 16 }}>
          <Text strong>{t("Supplier")}:</Text>
          <div>
            <Text>{supplierData.supplierName}</Text>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <Text strong>{t("Location")}:</Text>
          <div>
            <Text>{selectedLocation?.name}</Text>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <Text strong>{t("Billing Period")}:</Text>
          <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>
            {t("Select the month this expense belongs to")}
          </div>
          <DatePicker
            picker="month"
            value={dayjs()
              .year(customValue.year)
              .month(customValue.month - 1)}
            onChange={(date) => {
              if (date) {
                onValueChange({
                  month: date.month() + 1,
                  year: date.year(),
                  invoiceDate: date.startOf("month").format("YYYY-MM-DD"),
                });
              }
            }}
            format="MMMM YYYY"
            style={{ width: "100%" }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ display: "block", marginBottom: 4 }}>
            {t("Document Date")}
          </Text>
          <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>
            {t("Date on invoice, receipt or other document")}
          </div>
          <DatePicker
            value={dayjs(customValue.invoiceDate)}
            onChange={(date) => {
              if (date) {
                if (
                  date.month() + 1 === customValue.month &&
                  date.year() === customValue.year
                ) {
                  onValueChange({ invoiceDate: date.format("YYYY-MM-DD") });
                } else {
                  message.warning(
                    t("Document date must be within selected billing period"),
                  );
                }
              }
            }}
            format="DD.MM.YYYY"
            style={{ width: "100%" }}
            disabledDate={(current) => {
              if (!current) return false;
              return (
                current.month() + 1 !== customValue.month ||
                current.year() !== customValue.year
              );
            }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ display: "block", marginBottom: 4 }}>
            {t("Due Date")}
          </Text>
          <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>
            {t(
              "Payment deadline - invoice will be highlighted in red if unpaid after this date",
            )}
          </div>
          <DatePicker
            value={customValue.dueDate ? dayjs(customValue.dueDate) : null}
            onChange={(date) => {
              if (date) {
                onValueChange({ dueDate: date.format("YYYY-MM-DD") });
              } else {
                onValueChange({ dueDate: "" });
              }
            }}
            format="DD.MM.YYYY"
            style={{ width: "100%" }}
            placeholder={t("Select due date (optional)")}
            allowClear
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ display: "block", marginBottom: 8 }}>
            {t("Description")}
          </Text>
          <Input
            placeholder={t("Enter description")}
            value={customValue.description}
            onChange={(e) => onValueChange({ description: e.target.value })}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ display: "block", marginBottom: 8 }}>
            {t("Currency")}
          </Text>
          <Select
            value={customValue.currency}
            onChange={(currency) => onValueChange({ currency })}
            style={{ width: "100%" }}
          >
            <Option value="RSD">RSD</Option>
            <Option value="EUR">EUR</Option>
          </Select>
        </div>

        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ display: "block", marginBottom: 8 }}>
            {t("Amount")}
          </Text>
          <InputNumber
            min={0}
            placeholder="0"
            value={customValue.amount}
            onChange={(value) => onValueChange({ amount: Number(value || 0) })}
            style={{ width: "100%" }}
            formatter={(value) =>
              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            }
            parser={(value) => value?.replace(/,/g, "") as any}
          />
        </div>
      </div>
    </Modal>
  );
};

export default AddCustomValueModal;
